// Local device key storage (IndexedDB). Holds the unwrapped private key (JWK)
// and the network key (raw b64) so members stay unlocked across reloads on this device.
// Hardened browsers (Brave Shields / Private mode) may block IndexedDB, in that case
// every op degrades gracefully (reads return undefined, writes/clears are no-ops) so the
// session still works in-memory for this page load instead of crashing to a white screen.
import { indexedDbAvailable } from "./safeStorage";

const DB = "ss_keys";
const STORE = "keys";

function open() {
  return new Promise((resolve, reject) => {
    try {
      if (!indexedDbAvailable()) return reject(new Error("indexeddb-unavailable"));
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error("indexeddb-error"));
      req.onblocked = () => reject(new Error("indexeddb-blocked"));
    } catch (e) {
      reject(e);
    }
  });
}

async function put(key, val) {
  try {
    const db = await open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return; // persistence unavailable, session continues in-memory
  }
}

async function get(key) {
  try {
    const db = await open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const r = tx.objectStore(STORE).get(key);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  } catch {
    return undefined;
  }
}

async function clearAll() {
  try {
    const db = await open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return;
  }
}

export const keystore = {
  savePrivate: (uid, jwk) => put(`priv:${uid}`, jwk),
  loadPrivate: (uid) => get(`priv:${uid}`),
  saveNetwork: (uid, rawB64) => put(`net:${uid}`, rawB64),
  loadNetwork: (uid) => get(`net:${uid}`),
  clear: clearAll,
};
